import { ensureSession } from "./auth"
import { ensureActiveOrgForCurrentRoute } from "./active-event"
import { backendUrl } from "./backend-url"
import { apiFetch } from "./d1-bookmark"

// Typed fetch wrapper for /api/v1/*. The web admin authenticates with the
// Better Auth httpOnly cookie. Native apps use bearer tokens; the browser
// should not attach Authorization for normal CRUD calls.
//
// Mirrors the iOS `dataForQB` helper. All admin TanStack Query
// mutations + queries should go through this so 401-on-rotation is
// handled centrally.

export class ApiError extends Error {
  status: number
  code?: string
  body?: unknown

  constructor(message: string, status: number, code?: string, body?: unknown) {
    super(message)
    this.name = "ApiError"
    this.status = status
    this.code = code
    this.body = body
  }
}

type ApiInit = Omit<RequestInit, "body" | "headers"> & {
  body?: unknown
  headers?: Record<string, string>
  searchParams?: Record<string, string | number | boolean | undefined | null>
  signal?: AbortSignal
}

export async function api<T>(path: string, init: ApiInit = {}): Promise<T> {
  if (!path.startsWith("/")) path = `/${path}`
  const url = buildUrl(path, init.searchParams)

  const headers: Record<string, string> = {
    Accept: "application/json",
    ...(init.headers ?? {}),
  }
  if (init.body !== undefined && !(init.body instanceof FormData)) {
    headers["Content-Type"] = "application/json"
  }

  const send = async (): Promise<Response> => {
    const reqInit: RequestInit = {
      ...init,
      headers,
      credentials: "include",
      body:
        init.body === undefined
          ? undefined
          : init.body instanceof FormData
            ? init.body
            : JSON.stringify(init.body),
    }
    return apiFetch(url, reqInit)
  }

  // Sync the BA session's active org to the URL before minting a JWT, so
  // org-firewalled actions don't 403 during the gap between page mount and
  // useActiveEvent's effect resolving. The URL is the sole source of truth:
  // root `/` is org-less, `/<org>/...` pins that org. No-op on the root.
  //
  // `/me/*` are cross-tenant standalone actions (AUTHENTICATED, no active
  // org needed) — and `/me/admin-events` is the very list this hook awaits
  // to resolve the URL slug → eventId, so running it for that path would
  // deadlock. Skip the whole `/me/` namespace.
  if (!path.startsWith("/api/v1/me/")) {
    await ensureActiveOrgForCurrentRoute()
  }

  const session = await ensureSession()
  if (!session) throw new ApiError("Not signed in", 401, "NOT_SIGNED_IN")

  let res = await send()
  if (res.status === 401) {
    // Bypass the TTL cache — a 401 means our cached "signed in" belief is
    // stale, so re-confirm against the server before retrying.
    await ensureSession({ force: true })
    res = await send()
  }
  // 403 ACCESS_NO_ORG retry: a request can fire in the gap before the
  // URL-driven org activation lands (the events list is still loading, so
  // the slug → org resolution hasn't happened). Once
  // ensureActiveOrgForCurrentRoute completes (it awaits that list), the
  // session carries the right org — so re-await it, re-mint, retry once.
  if (res.status === 403) {
    const body = await res
      .clone()
      .json()
      .catch(() => null as null | { code?: string })
    if (body?.code === "ACCESS_NO_ORG") {
      await ensureActiveOrgForCurrentRoute()
      res = await send()
    }
  }
  return parseJsonOrThrow<T>(res)
}

function buildUrl(
  path: string,
  searchParams?: ApiInit["searchParams"]
): string {
  if (!searchParams) return backendUrl(path)
  const params = new URLSearchParams()
  for (const [k, v] of Object.entries(searchParams)) {
    if (v === undefined || v === null) continue
    params.append(k, String(v))
  }
  const qs = params.toString()
  return backendUrl(qs ? `${path}?${qs}` : path)
}

async function parseJsonOrThrow<T>(res: Response): Promise<T> {
  const ct = res.headers.get("Content-Type") ?? ""
  // Errors arrive as RFC 9457 `application/problem+json` (code + details as
  // extension members), so match any JSON media type, not just application/json.
  const isJson = /[/+]json\b/i.test(ct)
  const body: unknown = isJson ? await res.json().catch(() => null) : null
  if (!res.ok) {
    const obj = (body ?? {}) as { message?: string; error?: string; code?: string }
    throw new ApiError(
      obj.message ?? obj.error ?? `${res.status} ${res.statusText}`,
      res.status,
      obj.code,
      body
    )
  }
  return body as T
}
